import React, { useState } from "react";
import {
  Button,
  Table,
  Tag,
  message,
  Row,
  Col,
  Card,
  Statistic,
  Modal,
  Form,
  Input,
} from "antd";
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  PlusOutlined,
  EditOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  FetchProductsWithStock_api,
  UpdateProductStatus_api,
  UpdateStockStatus_api,
  AddProduct_api,
  UpdateProduct_api,
} from "../../Api/Products/product_api";
import { useAuthChecker } from "../../Context";

export const ManageProductsPage = () => {
  const { user } = useAuthChecker();
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchValue, setSearchValue] = useState("");

  // Modals
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any>(null);

  const [addProductLoading, setAddProductLoading] = useState(false);

  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();

  // Fetch products with stock
  const {
    data: productsData,
    refetch: refetchProducts,
    isFetching,
  } = useQuery({
    queryKey: ["products_with_stock", searchText],
    queryFn: () => FetchProductsWithStock_api(searchText),
  });

  const handleSearch = () => {
    setSearchText(searchValue);
  };

  const handleProductStatusToggle = async (
    productId: number,
    currentStatus: number,
  ) => {
    try {
      setLoading(true);
      const newStatus = currentStatus == 1 ? 0 : 1;

      const payload = {
        product_id: productId,
        status: newStatus,
        userId: user?.userId,
      };

      const res = await UpdateProductStatus_api(payload);
      if (res.isDataUpdated) {
        message.success(res.msg);
        refetchProducts();
      } else {
        message.error(res.msg);
      }
    } catch (error) {
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleStockStatusToggle = async (
    stockId: number,
    currentStatus: number,
  ) => {
    setLoading(true);
    try {
      const newStatus = currentStatus == 1 ? 0 : 1;

      const payload = {
        stock_id: stockId,
        status: newStatus,
        userId: user?.userId,
      };

      const res = await UpdateStockStatus_api(payload);
      if (res.isDataUpdated) {
        message.success(res.msg);
        refetchProducts();
      } else {
        message.error(res.msg);
      }
    } catch (error) {
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = async () => {
    setAddProductLoading(true);
    try {
      const values = await addForm.validateFields();
      const payload = {
        name: values.product_name,
        userId: user?.userId,
      };

      const res = await AddProduct_api(payload);
      if (res.isDataUpdated) {
        message.success(res.msg);
        setAddModalOpen(false);
        addForm.resetFields();
        refetchProducts();
      } else {
        message.error(res.msg);
      }
    } catch (error) {
      console.error("Product add error:", error);
    } finally {
      setAddProductLoading(false);
    }
  };

  const handleEditProduct = async () => {
    try {
      const values = await editForm.validateFields();
      const payload = {
        product_id: editingProduct.id,
        name: values.product_name,
        userId: user?.userId,
      };

      const res = await UpdateProduct_api(payload);
      if (res.isDataUpdated) {
        message.success(res.msg);
        setEditModalOpen(false);
        editForm.resetFields();
        setEditingProduct(null);
        refetchProducts();
      } else {
        message.error(res.msg);
      }
    } catch (error) {
      console.error("Product edit error:", error);
    }
  };

  const openEditModal = (product: any) => {
    setEditingProduct(product);
    editForm.setFieldsValue({ product_name: product.name });
    setEditModalOpen(true);
  };

  const stockColumns = [
    {
      title: "Cost",
      dataIndex: "cost",
      key: "cost",
      render: (cost: string) => `Rs. ${parseFloat(cost).toLocaleString()}`,
    },
    {
      title: "Quantity",
      dataIndex: "qty",
      key: "qty",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: number) => (
        <Tag color={status == 1 ? "green" : "red"}>
          {status == 1 ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: any) => (
        <Button
          loading={loading}
          size="small"
          type={record.status == 1 ? "default" : "primary"}
          danger={record.status == 1}
          onClick={() => handleStockStatusToggle(record.id, record.status)}
        >
          {record.status == 1 ? "Disable" : "Enable"}
        </Button>
      ),
    },
  ];

  const productColumns = [
    {
      title: "Product Name",
      dataIndex: "name",
      key: "name",
      render: (name: string) => <span className="font-semibold">{name}</span>,
    },
    {
      title: "Stock Items",
      key: "stock_count",
      render: (_: any, record: any) => (
        <span>{record.stock_items?.length || 0} items</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: number) => (
        <Tag color={status == 1 ? "green" : "red"}>
          {status == 1 ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: any) => (
        <div className="flex gap-2">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
          >
            Edit
          </Button>
          <Button
            loading={loading}
            size="small"
            type={record.status == 1 ? "default" : "primary"}
            danger={record.status == 1}
            onClick={() => handleProductStatusToggle(record.id, record.status)}
          >
            {record.status == 1 ? "Disable" : "Enable"}
          </Button>
        </div>
      ),
    },
  ];

  const stats = productsData?.stats || {};

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Manage Products</h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setAddModalOpen(true)}
        >
          Add Product
        </Button>
      </div>

      {/* Statistics Dashboard */}
      <Row gutter={16} className="mb-6">
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Products"
              value={stats.total_products || 0}
              prefix={<AppstoreOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active Products"
              value={stats.active_products || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Inactive Products"
              value={stats.inactive_products || 0}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: "#ff4d4f" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Stock Value"
              value={stats.total_stock_value || 0}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: "#faad14" }}
              suffix="LKR"
            />
          </Card>
        </Col>
      </Row>

      {/* Search Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4">
        <div className="flex gap-2">
          <Input
            placeholder="Search by product name..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onPressEnter={handleSearch}
            prefix={<SearchOutlined />}
            className="flex-1"
          />
          <Button type="primary" onClick={handleSearch}>
            Search
          </Button>
          {searchText && (
            <Button
              onClick={() => {
                setSearchValue("");
                setSearchText("");
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <Table
          className="overflow-x-auto"
          columns={productColumns}
          dataSource={productsData?.products || []}
          rowKey="id"
          loading={isFetching}
          pagination={false}
          expandable={{
            expandedRowRender: (record) => (
              <div className="p-4 bg-gray-50">
                <h3 className="text-sm font-semibold mb-3">Stock Items</h3>
                {record.stock_items && record.stock_items.length > 0 ? (
                  <Table
                    columns={stockColumns}
                    dataSource={record.stock_items}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    className="overflow-x-auto"
                  />
                ) : (
                  <p className="text-gray-500 text-sm">No stock items</p>
                )}
              </div>
            ),
            rowExpandable: (record) =>
              record.stock_items && record.stock_items.length > 0,
          }}
        />
      </div>

      {/* Add Product Modal */}
      <Modal
        title="Add Product"
        open={addModalOpen}
        onCancel={() => {
          setAddModalOpen(false);
          addForm.resetFields();
        }}
        onOk={handleAddProduct}
        loading={addProductLoading}
        okText="Add"
      >
        <Form form={addForm} layout="vertical">
          <Form.Item
            label="Product Name"
            name="product_name"
            rules={[{ required: true, message: "Product name is required" }]}
          >
            <Input placeholder="Enter product name" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Product Modal */}
      <Modal
        title="Edit Product"
        open={editModalOpen}
        onCancel={() => {
          setEditModalOpen(false);
          editForm.resetFields();
          setEditingProduct(null);
        }}
        onOk={handleEditProduct}
        okText="Update"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            label="Product Name"
            name="product_name"
            rules={[{ required: true, message: "Product name is required" }]}
          >
            <Input placeholder="Enter product name" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
